'use client';

/**
 * Hero Carousel - Automatic slideshow of portfolio images
 * Displays beautiful before/after project photos with smooth transitions
 */

import { useState, useEffect } from 'react';
import Image from 'next/image';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface CarouselImage {
  src: string;
  alt: string;
}

const heroImages: CarouselImage[] = [
  {
    src: '/images/uploads/a5ec00ee-0d7b-4046-be2a-cb682f449d8b.jpg',
    alt: 'Luxury furniture restoration project',
  },
  {
    src: '/images/uploads/e67b3da6-562c-444e-91a6-94c3caff4af1.jpg',
    alt: 'Custom sofa reupholstery',
  },
  {
    src: '/images/uploads/c1f1547e-0560-4d1b-b79f-f6624d09ce8e.jpg',
    alt: 'Professional window treatment installation',
  },
  {
    src: '/images/uploads/b90e0e70-991a-4636-b808-34c4c210b40e.jpg',
    alt: 'Elegant furniture craftsmanship',
  },
  {
    src: '/images/uploads/c9ec6d77-8635-4d90-a3fd-dadfa36139cc.jpg',
    alt: 'Beautiful interior design transformation',
  },
  {
    src: '/images/uploads/4061b253-61ab-436f-adcf-2299011ef5cb.jpg',
    alt: 'Luxury upholstery detail work',
  },
  {
    src: '/images/uploads/d55b5b4a-6e27-4e7c-a847-7f4f48af13e7.jpg',
    alt: 'Custom curtain installation',
  },
  {
    src: '/images/uploads/954a9859-2cc5-4abc-9813-39d94d00454b.jpg',
    alt: 'Professional furniture restoration',
  },
  {
    src: '/images/uploads/c8ca5635-98f8-4522-9adb-cefc127c685f.jpg',
    alt: 'Upholstery craftsmanship detail',
  },
  {
    src: '/images/uploads/33c5688c-4ad4-4ebe-85e3-af67d6396a38.jpg',
    alt: 'Interior design transformation',
  },
];

interface HeroCarouselProps {
  isBackground?: boolean;
}

export function HeroCarousel({ isBackground = false }: HeroCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlay, setIsAutoPlay] = useState(true);

  // Auto-play carousel
  useEffect(() => {
    if (!isAutoPlay) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % heroImages.length);
    }, 4000); // Change image every 4 seconds

    return () => clearInterval(interval);
  }, [isAutoPlay]);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + heroImages.length) % heroImages.length);
    setIsAutoPlay(false);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % heroImages.length);
    setIsAutoPlay(false);
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
    setIsAutoPlay(false);
  };

  if (isBackground) {
    return (
      <div className="absolute inset-0 w-full h-full overflow-hidden group">
        {/* Main carousel images */}
        <div className="relative w-full h-full">
          {heroImages.map((image, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                index === currentIndex ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <Image
                src={image.src}
                alt={image.alt}
                fill
                className="object-cover"
                priority={index === 0}
              />
              {/* Dark overlay for text readability */}
              <div className="absolute inset-0 bg-black/30" />
            </div>
          ))}
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={goToPrevious}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-black/40 hover:bg-black/60 text-white p-2 rounded-full transition-all duration-300 opacity-0 group-hover:opacity-100"
          aria-label="Previous image"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>

        <button
          onClick={goToNext}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-black/40 hover:bg-black/60 text-white p-2 rounded-full transition-all duration-300 opacity-0 group-hover:opacity-100"
          aria-label="Next image"
        >
          <ChevronRight className="h-6 w-6" />
        </button>

        {/* Dot indicators */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex gap-2">
          {heroImages.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`h-2.5 rounded-full transition-all duration-300 ${
                index === currentIndex
                  ? 'bg-white w-8'
                  : 'bg-white/50 hover:bg-white/75 w-2.5'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Image counter */}
        <div className="absolute top-4 right-4 z-20 bg-black/40 text-white px-4 py-2 rounded-full text-sm font-medium">
          {currentIndex + 1} / {heroImages.length}
        </div>

        {/* Auto-play indicator */}
        <div
          className="absolute bottom-0 left-0 h-1 bg-white/60 transition-all duration-300 ease-linear"
          style={{
            width: isAutoPlay ? '100%' : '0%',
            animation: isAutoPlay ? `progress 4s linear infinite` : 'none',
          }}
        />

        <style>{`
          @keyframes progress {
            0% { width: 100%; }
            100% { width: 0%; }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="relative w-full h-[400px] md:h-[500px] lg:h-[550px] rounded-lg overflow-hidden group">
      {/* Main carousel images */}
      <div className="relative w-full h-full">
        {heroImages.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              index === currentIndex ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <Image
              src={image.src}
              alt={image.alt}
              fill
              className="object-cover"
              priority={index === 0}
            />
            {/* Subtle overlay for text readability */}
            <div className="absolute inset-0 bg-black/10" />
          </div>
        ))}
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={goToPrevious}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-black/40 hover:bg-black/60 text-white p-2 rounded-full transition-all duration-300 opacity-0 group-hover:opacity-100"
        aria-label="Previous image"
      >
        <ChevronLeft className="h-6 w-6" />
      </button>

      <button
        onClick={goToNext}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-black/40 hover:bg-black/60 text-white p-2 rounded-full transition-all duration-300 opacity-0 group-hover:opacity-100"
        aria-label="Next image"
      >
        <ChevronRight className="h-6 w-6" />
      </button>

      {/* Dot indicators */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex gap-2">
        {heroImages.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`h-2.5 rounded-full transition-all duration-300 ${
              index === currentIndex
                ? 'bg-white w-8'
                : 'bg-white/50 hover:bg-white/75 w-2.5'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Image counter */}
      <div className="absolute top-4 right-4 z-10 bg-black/40 text-white px-4 py-2 rounded-full text-sm font-medium">
        {currentIndex + 1} / {heroImages.length}
      </div>

      {/* Auto-play indicator */}
      <div
        className="absolute bottom-0 left-0 h-1 bg-white/60 transition-all duration-300 ease-linear"
        style={{
          width: isAutoPlay ? '100%' : '0%',
          animation: isAutoPlay ? `progress 4s linear infinite` : 'none',
        }}
      />

      <style>{`
        @keyframes progress {
          0% { width: 100%; }
          100% { width: 0%; }
        }
      `}</style>
    </div>
  );
}
